import { graphql } from "@/gql";
import { OrderByEnum, type AuditFilterInput } from "@/gql/graphql";
import { useQuery } from "@/utils/graphql";
import { formatTime } from "@/utils/time";
import {
  Alert,
  Input,
  Pagination,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const GET_AUDITS = graphql(`
  query GetAdminAudits(
    $limit: Int!
    $page: Int!
    $orderBy: AuditOrderInput!
    $filters: AuditFilterInput!
  ) {
    audits(
      pagination: { page: { limit: $limit, page: $page } }
      orderBy: $orderBy
      filters: $filters
    ) {
      nodes {
        auditId
        tableName
        operationType
        operatorName
        createdOn
      }
      paginationInfo {
        total
      }
    }
  }
`);

export default function AuditsPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchOperator, setSearchOperator] = useState("");
  const [sortField, setSortField] = useState<"createdOn" | "operatorName">(
    "createdOn",
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const orderByObj =
    sortField === "createdOn"
      ? { createdOn: sortOrder === "asc" ? OrderByEnum.Asc : OrderByEnum.Desc }
      : {
          operatorName:
            sortOrder === "asc" ? OrderByEnum.Asc : OrderByEnum.Desc,
        };

  const filters: AuditFilterInput = {};
  if (searchOperator.trim()) {
    filters.operatorName = { ilike: `%${searchOperator}%` };
  }

  const { data, isLoading, error } = useQuery(GET_AUDITS, {
    limit: pageSize,
    page,
    orderBy: orderByObj,
    filters,
  });

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        title={t("common.error", { message: error.message })}
      />
    );
  }

  const dataSource = (data?.audits?.nodes ?? []).map((node) => ({
    key: node.auditId,
    ...node,
  }));

  const total = data?.audits?.paginationInfo?.total ?? 0;

  return (
    <Space orientation="vertical" size="large" className="w-full">
      <Typography.Title level={3} className="m-0">
        {t("nav.auditLogs")}
      </Typography.Title>

      {/* Filter Section */}
      <Space wrap>
        <Input.Search
          placeholder={t("common.search")}
          value={searchOperator}
          onChange={(e) => {
            setSearchOperator(e.target.value);
            setPage(0);
          }}
          style={{ width: 200 }}
          allowClear
        />
        <Select
          value={sortField}
          onChange={(value) => {
            setSortField(value);
            setPage(0);
          }}
          style={{ width: 140 }}
          options={[
            { label: t("common.createdAt"), value: "createdOn" },
            { label: t("common.operator"), value: "operatorName" },
          ]}
        />
        <Select
          value={sortOrder}
          onChange={(value) => {
            setSortOrder(value);
            setPage(0);
          }}
          style={{ width: 100 }}
          options={[
            { label: t("common.ascending"), value: "asc" },
            { label: t("common.descending"), value: "desc" },
          ]}
        />
      </Space>

      <Table
        rowKey="auditId"
        loading={isLoading}
        dataSource={dataSource}
        pagination={false}
        columns={[
          { title: t("common.id"), dataIndex: "auditId" },
          { title: t("common.table"), dataIndex: "tableName" },
          {
            title: t("common.operation"),
            dataIndex: "operationType",
            render: (value: string) => <Tag>{value}</Tag>,
          },
          { title: t("common.operator"), dataIndex: "operatorName" },
          {
            title: t("common.createdAt"),
            dataIndex: "createdOn",
            render: (value: string) => formatTime(value),
          },
        ]}
      />

      {/* Pagination */}
      <Pagination
        current={page + 1}
        pageSize={pageSize}
        total={total}
        onChange={(current) => setPage(current - 1)}
        onShowSizeChange={(_, size) => {
          setPageSize(size);
          setPage(0);
        }}
        showSizeChanger
        showTotal={(total) => `${t("common.total")} ${total}`}
      />
    </Space>
  );
}
